// Un concepto = una línea del CFDI = una fila del reporte FACTURAS PENDIENTES.
export interface BillingConcepto {
  position: number;                    // Detalle - Posición
  quantity: number;                    // Detalle - Cantidad
  key: string;                         // Detalle - Clave (catálogo Producto/Servicio)
  unit: 'PRODUCTO' | 'SERVICIO';       // Detalle - Unidad
  unitPrice: number;                   // Detalle - Precio Unitario (SIN IVA)
  comments?: string;                   // Comentarios (default RENTA {MES} {AÑO} al exportar)
}

export interface Billing {
  _id: string;
  // businessName / contractNumber quedan DEPRECADOS como captura: el reporte deriva
  // Razón Social del cliente y No. Contrato del arrendamiento. Opcionales por compatibilidad.
  businessName?: string;
  comments?: string;
  paymentMethod: string;
  paymentType: string;
  cfdiUsage: string;
  contractNumber?: string;
  conceptos?: BillingConcepto[];
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateBillingDto {
  paymentMethod: string;
  paymentType: string;
  cfdiUsage: string;
  conceptos?: BillingConcepto[];
  leaseId?: string;
}

// Datos fiscales derivados (read-only) que la tab muestra en el "Resumen fiscal".
// Razón Social ← cliente registrado · No. Contrato ← lease.contract · No. ID ← lease.oldId.
export interface BillingFiscalSummary {
  razonSocial: string;
  rfc: string;
  contractNumber: string;
  oldId: string;
}

export interface UpdateBillingDto extends Partial<CreateBillingDto> {}

// Ítem del catálogo Producto/Servicio (dropdown de Clave). Backend: catalogs/product-services.
export interface ProductServiceCatalogItem {
  _id: string;
  key: string;
  name: string;
  type: 'PRODUCTO' | 'SERVICIO';
  claveProdServ?: string;
  claveUnidad?: string;
  active?: boolean;
}

export interface BillingFilters {
  page: number;
  limit: number;
  filters: {
    [key: string]: {
      value: any;
      operator: string;
    };
  };
  search?: string;
}

export interface PaginatedResponse<T> {
  results: T[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Catálogos SAT
export interface SATCatalog {
  value: string;
  label: string;
  description?: string;
}

export const PAYMENT_METHODS: SATCatalog[] = [
  { value: '01', label: '01 - Efectivo' },
  { value: '02', label: '02 - Cheque nominativo' },
  { value: '03', label: '03 - Transferencia electrónica de fondos' },
  { value: '04', label: '04 - Tarjeta de crédito' },
  { value: '05', label: '05 - Monedero electrónico' },
  { value: '06', label: '06 - Dinero electrónico' },
  { value: '08', label: '08 - Vales de despensa' },
  { value: '12', label: '12 - Dación en pago' },
  { value: '13', label: '13 - Pago por subrogación' },
  { value: '14', label: '14 - Pago por consignación' },
  { value: '15', label: '15 - Condonación' },
  { value: '17', label: '17 - Compensación' },
  { value: '23', label: '23 - Novación' },
  { value: '24', label: '24 - Confusión' },
  { value: '25', label: '25 - Remisión de deuda' },
  { value: '26', label: '26 - Prescripción o caducidad' },
  { value: '27', label: '27 - A satisfacción del acreedor' },
  { value: '28', label: '28 - Tarjeta de débito' },
  { value: '29', label: '29 - Tarjeta de servicios' },
  { value: '30', label: '30 - Aplicación de anticipos' },
  { value: '31', label: '31 - Intermediario pagos' },
  { value: '99', label: '99 - Por definir' }
];

export const PAYMENT_TYPES: SATCatalog[] = [
  { value: 'PUE', label: 'PUE - Pago en una sola exhibición' },
  { value: 'PPD', label: 'PPD - Pago en parcialidades o diferido' }
];

export const CFDI_USAGES: SATCatalog[] = [
  { value: 'G01', label: 'G01 - Adquisición de mercancías' },
  { value: 'G02', label: 'G02 - Devoluciones, descuentos o bonificaciones' },
  { value: 'G03', label: 'G03 - Gastos en general' },
  { value: 'I01', label: 'I01 - Construcciones' },
  { value: 'I02', label: 'I02 - Mobilario y equipo de oficina por inversiones' },
  { value: 'I03', label: 'I03 - Equipo de transporte' },
  { value: 'I04', label: 'I04 - Equipo de computo y accesorios' },
  { value: 'I05', label: 'I05 - Dados, troqueles, moldes, matrices y herramental' },
  { value: 'I06', label: 'I06 - Comunicaciones telefónicas' },
  { value: 'I07', label: 'I07 - Comunicaciones satelitales' },
  { value: 'I08', label: 'I08 - Otra maquinaria y equipo' },
  { value: 'D01', label: 'D01 - Honorarios médicos, dentales y gastos hospitalarios' },
  { value: 'D02', label: 'D02 - Gastos médicos por incapacidad o discapacidad' },
  { value: 'D03', label: 'D03 - Gastos funerales' },
  { value: 'D04', label: 'D04 - Donativos' },
  { value: 'D05', label: 'D05 - Intereses reales efectivamente pagados por créditos hipotecarios' },
  { value: 'D06', label: 'D06 - Aportaciones voluntarias al SAR' },
  { value: 'D07', label: 'D07 - Primas por seguros de gastos médicos' },
  { value: 'D08', label: 'D08 - Gastos de transportación escolar obligatoria' },
  { value: 'D09', label: 'D09 - Depósitos en cuentas para el ahorro' },
  { value: 'D10', label: 'D10 - Pagos por servicios educativos' },
  { value: 'P01', label: 'P01 - Por definir' },
  { value: 'S01', label: 'S01 - Sin efectos fiscales' },
  { value: 'CP01', label: 'CP01 - Pagos' },
  { value: 'CN01', label: 'CN01 - Nómina' }
];